import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError } from "../lib/api";
import { Panel, SectionTitle, TactLoader } from "../components/Tactical";
import { toast } from "sonner";
import {
  Loader2, Smartphone, Monitor, RefreshCw, Trophy, Crown, Trash2,
} from "lucide-react";

/**
 * Admin bracket map for the 48-team World Cup test tournament.
 *
 * Renders the FULL knockout tree (R32 → R16 → QF → SF → F + 3rd-place
 * playoff = 31 matches) at every tournament stage:
 *  - forming / group_stage: every cell is a placeholder showing the
 *    projected seed slot (e.g. "1st-placed #1", "Best 3rd-placed #8").
 *    When a group is partially settled, the leader of that group is shown
 *    as a "provisional" projection in the matching R32 slot.
 *  - knockout / done: cells fill with the real qualified players + their
 *    drawn nation flag and live confirmed scores.
 *
 * Also shows the live group standings table for both pools so the admin
 * can see at a glance who's about to qualify from where.
 */
export default function AdminWCTourneyTab() {
  const [platform, setPlatform] = useState("mobile");
  // iter 96 — a platform spawns a fresh 48-slot tournament per batch, so we
  // let the admin pick WHICH tournament to view. null = latest (default).
  const [tournamentId, setTournamentId] = useState(null);
  const switchPlatform = (p) => { setPlatform(p); setTournamentId(null); };
  return (
    <div className="space-y-6" data-testid="admin-wctourney-root">
      <SectionTitle kicker="// admin control" testId="admin-wctourney-title">
        World Cup Test — Bracket Map
      </SectionTitle>
      <div className="flex gap-2" data-testid="admin-wctourney-platform-tabs">
        <PlatformBtn cur={platform} setCur={switchPlatform} value="mobile"  label="Mobile"  Icon={Smartphone} />
        <PlatformBtn cur={platform} setCur={switchPlatform} value="console" label="Console" Icon={Monitor} />
      </div>
      <TournamentSelector platform={platform} selected={tournamentId} onSelect={setTournamentId} />
      <BracketPreview platform={platform} tournamentId={tournamentId} />
    </div>
  );
}

/* =========================================================================
   TOURNAMENT SELECTOR — list every tournament for the platform (iter 96)
   ========================================================================= */
function TournamentSelector({ platform, selected, onSelect }) {
  const [list, setList] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/wctourney/admin/${platform}/tournaments`);
      setList(data.tournaments || []);
    } catch (e) {
      toast.error(formatApiError(e));
      setList([]);
    }
  }, [platform]);

  useEffect(() => { setList(null); load(); }, [load]);

  if (list === null) return <TactLoader />;
  if (list.length <= 1) return null; // nothing to switch between

  const chip = (active, key, label, onClick, testId) => (
    <button
      key={key}
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest transition-colors ${
        active
          ? "border-[#FFD700] text-[#FFD700] bg-[#FFD700]/10"
          : "border-[#1F1F2A] text-[#A1A1AA] hover:text-[#09090B] dark:hover:text-[#F4F4F5]"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="space-y-1.5" data-testid="admin-wctourney-selector">
      <div className="text-[10px] font-mono uppercase tracking-widest text-[#71717A]">
        // {list.length} {platform} tournaments
      </div>
      <div className="flex flex-wrap gap-2">
        {chip(selected === null, "latest", "Latest", () => onSelect(null), "admin-wctourney-tsel-latest")}
        {list.map((t) => chip(
          selected === t.id,
          t.id,
          `#${t.seq} · ${(t.status || "").replace(/_/g, " ")} · ${t.filled}/${t.slots || 48}${t.champion_id ? " · 🏆" : ""}`,
          () => onSelect(t.id),
          `admin-wctourney-tsel-${t.seq}`,
        ))}
      </div>
    </div>
  );
}

function PlatformBtn({ cur, setCur, value, label, Icon }) {
  const active = cur === value;
  return (
    <button
      type="button"
      data-testid={`admin-wctourney-platform-${value}`}
      onClick={() => setCur(value)}
      className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-[11px] font-mono uppercase tracking-[0.25em] transition-colors ${
        active
          ? "border-[#FFD700] text-[#FFD700] bg-[#FFD700]/10"
          : "border-[#1F1F2A] text-[#A1A1AA] hover:text-[#09090B] dark:hover:text-[#F4F4F5]"
      }`}
    >
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );
}

function BracketPreview({ platform, tournamentId = null }) {
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true);
    try {
      const qs = tournamentId ? `?tournament_id=${encodeURIComponent(tournamentId)}` : "";
      const { data } = await api.get(`/wctourney/admin/${platform}/bracket-preview${qs}`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setData({ tournament: null, groups: [], seeds: [], bracket: [] });
    } finally {
      setRefreshing(false);
    }
  }, [platform, tournamentId]);

  useEffect(() => { setData(null); load(); }, [load]);

  if (data === null) return <TactLoader />;

  const t = data.tournament;
  const totalFilled = (data.seeds || []).filter((s) => s.filled).length;

  return (
    <div className="space-y-6">
      {/* Tournament header strip */}
      <div className="rounded-2xl bg-white dark:bg-[#12121C] border border-[#E4E4E7] dark:border-[#1F1F2A] p-4 flex flex-wrap items-center justify-between gap-3" data-testid="admin-wctourney-header">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#71717A]">{platform.toUpperCase()} pool</div>
          {t ? (
            <div className="text-sm font-bold text-[#09090B] dark:text-white">
              {t.status.replace(/_/g, " ")} · {t.filled}/48 joined · {totalFilled}/32 qualifiers known
            </div>
          ) : (
            <div className="text-sm font-bold text-[#71717A]">No tournament in this pool yet.</div>
          )}
        </div>
        <button
          type="button"
          onClick={() => load(true)}
          disabled={refreshing}
          data-testid="admin-wctourney-refresh"
          className="inline-flex items-center gap-1.5 rounded-full border border-[#1F1F2A] text-[10px] font-mono uppercase tracking-widest px-3 py-1.5 text-[#A1A1AA] hover:text-white transition-colors disabled:opacity-50"
        >
          {refreshing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Refresh
        </button>
      </div>

      {/* Group standings — collapsible 12 cards */}
      {(data.groups || []).length > 0 && <GroupsGrid groups={data.groups} platform={platform} onChanged={() => load(true)} />}

      {/* Full bracket map */}
      {(data.bracket || []).length > 0 && <BracketMap bracket={data.bracket} seeds={data.seeds || []} />}
    </div>
  );
}

/* =========================================================================
   GROUPS GRID — 12 mini-cards
   ========================================================================= */
function GroupsGrid({ groups, platform, onChanged }) {
  return (
    <div className="space-y-2" data-testid="admin-wctourney-groups">
      <div className="text-[10px] font-mono uppercase tracking-widest text-[#FFD700]">// Live group standings</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {groups.map((g) => <GroupCard key={g.group} g={g} platform={platform} onChanged={onChanged} />)}
      </div>
    </div>
  );
}

function GroupCard({ g, platform, onChanged }) {
  const [busyId, setBusyId] = useState(null);
  const removePlayer = async (r) => {
    if (!window.confirm(
      `Remove @${r.username || r.user_id.slice(0, 6)} (${r.team?.name || r.team?.flag || ""}) ` +
      `from the ${platform} Tournament?\n\nTheir slot is freed and open matches are forfeited.`
    )) return;
    setBusyId(r.user_id);
    try {
      await api.post("/admin/wc/kick", { mode: "tourney", platform, user_id: r.user_id });
      toast.success(`Removed @${r.username || r.user_id.slice(0, 6)}`);
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(null); }
  };
  return (
    <div className="rounded-2xl bg-white dark:bg-[#12121C] border border-[#E4E4E7] dark:border-[#1F1F2A] overflow-hidden" data-testid={`admin-wctourney-group-${g.group}`}>
      <div className="px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest text-[#FFD700] border-b border-[#E4E4E7] dark:border-[#1F1F2A] flex items-center justify-between">
        <span>Group {g.group}</span>
        <span className="text-[#71717A]">{g.rows.length}/4</span>
      </div>
      {g.rows.length === 0 ? (
        <div className="px-3 py-3 text-[11px] text-[#52525B] italic">— empty —</div>
      ) : (
        <table className="w-full text-[11px]">
          <thead>
            <tr className="text-[9px] font-mono uppercase tracking-widest text-[#71717A]">
              <th className="text-left px-2 py-1">#</th>
              <th className="text-left">Player</th>
              <th className="text-center">P</th>
              <th className="text-center">W-D-L</th>
              <th className="text-center">GD</th>
              <th className="text-right px-2 py-1">Pts</th>
              <th className="px-2 py-1"></th>
            </tr>
          </thead>
          <tbody>
            {g.rows.map((r) => {
              const qualifyStyle = r.pos === 1 ? "text-[#00FF87]" : r.pos === 2 ? "text-[#A5B4FC]" : r.pos === 3 ? "text-[#FBBF24]" : "text-[#71717A]";
              return (
                <tr key={r.user_id} className="border-t border-[#E4E4E7] dark:border-[#1F1F2A]">
                  <td className={`px-2 py-1.5 font-mono ${qualifyStyle}`}>{r.pos}</td>
                  <td className="font-bold text-[#09090B] dark:text-white">
                    <span className="mr-1">{r.team?.flag || "🏳️"}</span> @{r.username || r.user_id.slice(0,6)}
                  </td>
                  <td className="text-center tabular-nums">{r.played}</td>
                  <td className="text-center tabular-nums text-[#71717A]">{r.wins}-{r.draws}-{r.losses}</td>
                  <td className="text-center tabular-nums">{r.gd > 0 ? `+${r.gd}` : r.gd}</td>
                  <td className="text-right px-2 py-1.5 font-bold tabular-nums">{r.points}</td>
                  <td className="px-2 py-1 text-center">
                    <button
                      type="button"
                      onClick={() => removePlayer(r)}
                      disabled={busyId === r.user_id}
                      data-testid={`admin-wctourney-remove-${r.user_id}`}
                      className="inline-flex items-center justify-center rounded-md p-1 text-rose-600 dark:text-rose-300 hover:bg-rose-500/15 disabled:opacity-40"
                      title="Remove player"
                    >
                      {busyId === r.user_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

/* =========================================================================
   BRACKET MAP — column-per-round visualisation
   ========================================================================= */
const ROUND_ORDER = ["R32", "R16", "QF", "SF", "F"];
const ROUND_LABELS = { R32: "Round of 32", R16: "Round of 16", QF: "Quarter-finals", SF: "Semi-finals", F: "Final" };

function BracketMap({ bracket, seeds }) {
  const byRound = useMemo(() => {
    const m = { R32: [], R16: [], QF: [], SF: [], F: [], "3RD": [] };
    for (const cell of bracket) (m[cell.round] = m[cell.round] || []).push(cell);
    for (const k of Object.keys(m)) m[k].sort((a, b) => a.bracket_pos - b.bracket_pos);
    return m;
  }, [bracket]);

  return (
    <div className="space-y-3" data-testid="admin-wctourney-bracket">
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#FFD700]">// Knockout bracket map</div>
        <Legend />
      </div>

      <div className="overflow-x-auto pb-3">
        <div className="flex gap-4 min-w-max" style={{ minWidth: "1100px" }}>
          {ROUND_ORDER.map((rnd) => (
            <RoundColumn key={rnd} round={rnd} cells={byRound[rnd] || []} />
          ))}
        </div>
      </div>

      {/* 3rd-place playoff under the bracket */}
      {(byRound["3RD"] || []).length > 0 && (
        <div className="rounded-2xl bg-[#12121C] border border-[#FBBF24]/30 p-3 max-w-md mx-auto" data-testid="admin-wctourney-3rd">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#FBBF24] mb-2">// 3rd-place playoff</div>
          <BracketCell cell={byRound["3RD"][0]} />
        </div>
      )}
    </div>
  );
}

function Legend() {
  return (
    <div className="text-[9px] font-mono uppercase tracking-widest text-[#71717A] flex flex-wrap gap-3" data-testid="admin-wctourney-legend">
      <span><span className="text-[#00FF87]">●</span> 1st-place</span>
      <span><span className="text-[#A5B4FC]">●</span> 2nd-place</span>
      <span><span className="text-[#FBBF24]">●</span> 3rd-place</span>
      <span><span className="text-[#71717A]">○</span> placeholder</span>
    </div>
  );
}

function RoundColumn({ round, cells }) {
  return (
    <div className="flex flex-col gap-2" style={{ width: "210px" }} data-testid={`admin-wctourney-col-${round}`}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-[#FFD700] text-center pb-1 border-b border-[#1F1F2A]">
        {ROUND_LABELS[round]}
      </div>
      <div className="flex flex-col justify-around flex-1 gap-2">
        {cells.map((c) => <BracketCell key={`${c.round}-${c.bracket_pos}`} cell={c} />)}
      </div>
    </div>
  );
}

function BracketCell({ cell }) {
  const winner = cell.winner_id;
  const live = cell.status && cell.status !== "placeholder";
  return (
    <div
      className={`rounded-xl border overflow-hidden text-[11px] ${
        live
          ? winner
            ? "border-[#00FF87]/50 bg-[#00FF87]/5"
            : "border-[#FBBF24]/40 bg-[#FBBF24]/5"
          : "border-[#1F1F2A] bg-[#0F0F18]"
      }`}
      data-testid={`admin-wctourney-cell-${cell.round}-${cell.bracket_pos}`}
    >
      <div className="px-2 py-0.5 text-[9px] font-mono uppercase tracking-widest text-[#71717A] flex justify-between border-b border-[#1F1F2A]">
        <span>{cell.round} M{cell.bracket_pos}</span>
        <span>
          {live
            ? cell.status === "confirmed"
              ? "FT"
              : cell.status === "in_progress"
                ? "LIVE"
                : cell.status
            : "TBD"}
        </span>
      </div>
      <SlotRow
        side="p1"
        winner={winner}
        cell={cell}
        username={cell.p1_username}
        team={cell.p1_team}
        projected={cell.p1_projected}
        label={cell.p1_label}
        score={cell.confirmed_score_p1}
        userId={cell.p1_id}
      />
      <div className="h-px bg-[#1F1F2A]" />
      <SlotRow
        side="p2"
        winner={winner}
        cell={cell}
        username={cell.p2_username}
        team={cell.p2_team}
        projected={cell.p2_projected}
        label={cell.p2_label}
        score={cell.confirmed_score_p2}
        userId={cell.p2_id}
      />
    </div>
  );
}

function SlotRow({ side, winner, cell, username, team, projected, label, score, userId }) {
  const isWinner = winner && userId && winner === userId;
  const realPlayer = !!userId;
  const provisional = !realPlayer && projected;

  let display;
  if (realPlayer) {
    display = (
      <span className={`truncate ${isWinner ? "text-[#00FF87] font-bold" : "text-white"}`}>
        <span className="mr-1">{team?.flag || "🏳️"}</span>@{username || (userId ? userId.slice(0,6) : "?")}
      </span>
    );
  } else if (provisional) {
    display = (
      <span className="truncate text-[#A5B4FC]">
        <span className="mr-1 opacity-80">{projected.team?.flag || "🏳️"}</span>
        @{projected.username || projected.user_id?.slice(0,6)}
        <span className="ml-1 text-[9px] text-[#71717A] uppercase tracking-widest">·prov</span>
      </span>
    );
  } else {
    display = <span className="truncate text-[#71717A] italic">{label || "TBD"}</span>;
  }

  return (
    <div
      className="flex items-center justify-between px-2 py-1 min-w-0 gap-1"
      data-testid={`admin-wctourney-cell-${cell.round}-${cell.bracket_pos}-${side}`}
    >
      {display}
      {score != null && (
        <span className={`flex-shrink-0 tabular-nums text-[11px] font-bold ${isWinner ? "text-[#00FF87]" : "text-white/80"}`}>
          {score}
        </span>
      )}
    </div>
  );
}
